import { Routes, Route, Navigate } from 'react-router-dom';
import { useSession } from '@/lib/auth';
import { AppShell } from '@/components/layout/AppShell';
import Login from '@/routes/Login';
import Today from '@/routes/Today';
import Submissions from '@/routes/Submissions';
import Revisions from '@/routes/Revisions';
import PendingApprovals from '@/routes/PendingApprovals';
import ActiveStores from '@/routes/ActiveStores';
import Products from '@/routes/Products';
import MultiLogo from '@/routes/MultiLogo';
import PrizeProducts from '@/routes/PrizeProducts';
import LogoLibrary from '@/routes/LogoLibrary';
import Schools from '@/routes/Schools';
import Configuration from '@/routes/Configuration';
import Templates from '@/routes/Templates';
import DesignStudio from '@/routes/DesignStudio';
import TemplateAuthor from '@/routes/TemplateAuthor';
import StoreTemplateEditor from '@/routes/StoreTemplateEditor';
import ProductTemplateEditor from '@/routes/ProductTemplateEditor';
import StoreBuilder from '@/routes/StoreBuilder';
import PersonalizationTemplates from '@/routes/PersonalizationTemplates';
import PersonalizationAssign from '@/routes/PersonalizationAssign';
import PersonalizationBilling from '@/routes/PersonalizationBilling';
import Recipes from '@/routes/Recipes';
import Pricing from '@/routes/Pricing';
import PlayerPacks from '@/routes/PlayerPacks';
import SupplierCatalog from '@/routes/SupplierCatalog';
import CreateStore from '@/routes/CreateStore';
import StorePage from '@/routes/StorePage';
import Reports from '@/routes/Reports';
import Payouts from '@/routes/Payouts';
import Chat from '@/routes/Chat';
import BulkImport from '@/routes/BulkImport';
import Pipeline from '@/routes/Pipeline';
import Outreach from '@/routes/Outreach';
import TeamStoreSubmissions from '@/routes/TeamStoreSubmissions';
import Ops from '@/routes/Ops';
import AgentTasks from '@/routes/AgentTasks';
import Ordering from '@/routes/Ordering';
import Orders from '@/routes/Orders';
import Reorder from '@/routes/Reorder';
import Samples from '@/routes/Samples';

function RequireAuth({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useSession();
  if (isLoading) return <div className="flex h-full items-center justify-center text-subtle">Loading…</div>;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

export default function App() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      {/* No /coach/:storeId route here on purpose. server.js mounts
          routes/coach.js's page router at /coach BEFORE the SPA fallback, so
          the server-rendered coach page always answers that URL and an SPA
          route would be permanently shadowed. CoachStore.tsx stays ready for
          the day the Express page is retired. */}
      <Route element={<RequireAuth><AppShell /></RequireAuth>}>
        <Route index element={<Today />} />

        {/* Operations — one queue per channel, action drawers on every row */}
        <Route path="submissions" element={<Submissions />} />
        <Route path="team-store-submissions" element={<TeamStoreSubmissions />} />
        <Route path="revisions" element={<Revisions />} />
        <Route path="pending-approvals" element={<PendingApprovals />} />

        {/* Old Worker Review paths (pre-2026-06-10 bookmarks) */}
        <Route path="worker-review/submissions" element={<Navigate to="/submissions" replace />} />
        <Route path="worker-review/team-stores" element={<Navigate to="/team-store-submissions" replace />} />

        {/* Ordering cluster — parity workstream B (real Shopify order mutations) */}
        <Route path="ordering" element={<Ordering />} />
        <Route path="orders" element={<Orders />} />
        <Route path="reorder" element={<Reorder />} />
        <Route path="samples" element={<Samples />} />

        {/* Team Stores — /store/:id is the full page; /active-stores/:id stays as an alias */}
        <Route path="active-stores" element={<ActiveStores />} />
        <Route path="store/:id" element={<StorePage />} />
        <Route path="active-stores/:id" element={<StorePage />} />
        <Route path="chat" element={<Chat />} />
        <Route path="create-store" element={<CreateStore />} />
        <Route path="templates" element={<Templates />} />
        <Route path="design-studio" element={<DesignStudio />} />
        <Route path="store-builder" element={<StoreBuilder />} />
        <Route path="template-author" element={<TemplateAuthor />} />
        <Route path="store-template-editor" element={<StoreTemplateEditor />} />
        <Route path="product-template-editor" element={<ProductTemplateEditor />} />

        {/* Personalization (name/number on garments) */}
        <Route path="personalization/templates" element={<PersonalizationTemplates />} />
        <Route path="personalization/assign" element={<PersonalizationAssign />} />
        <Route path="personalization/billing" element={<PersonalizationBilling />} />
        <Route path="reports" element={<Reports />} />
        <Route path="payouts" element={<Payouts />} />

        {/* Catalog */}
        <Route path="products" element={<Products />} />
        <Route path="multi-logo" element={<MultiLogo />} />
        <Route path="prize-products" element={<PrizeProducts />} />
        <Route path="bulk-import" element={<BulkImport />} />
        <Route path="supplier-catalog" element={<SupplierCatalog />} />
        <Route path="logos" element={<LogoLibrary />} />

        {/* Scout — Pipeline + Outreach are derived views that live in scout-dashboard */}
        <Route path="schools" element={<Schools />} />
        <Route path="pipeline" element={<Pipeline />} />
        <Route path="outreach" element={<Outreach />} />

        {/* System — read-only ops health (workstream C) */}
        <Route path="ops" element={<Ops />} />
        <Route path="agent" element={<AgentTasks />} />

        {/* Settings */}
        <Route path="recipes" element={<Recipes />} />
        <Route path="configuration" element={<Configuration />} />
        <Route path="pricing" element={<Pricing />} />
        <Route path="player-packs" element={<PlayerPacks />} />

        <Route path="*" element={
          <div className="px-7 py-6">
            <h1 className="text-[20px] font-semibold tracking-tighter">Not found</h1>
            <p className="mt-1 text-[12px] text-muted">That page doesn't exist in the console yet.</p>
          </div>
        } />
      </Route>
    </Routes>
  );
}
